/* eslint-disable no-nested-ternary */
import ColorScale from "color-scales";
import getTrailingReturns from "components/issuers/trailingReturns/getTrailingReturns";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/Popover";
import classNames from "components/ui/classNames";
import { tailwindColors } from "components/ui/colors/colorsBundle";
import useSelfie from "lib/hooks/useSelfie";
import { CircleIcon, LockIcon } from "lucide-react";
import { useState } from "react";
import { useIntl } from "react-intl";
import type {
  IAmericanCompanyIssuer,
  IBrazilianCompanyIssuer,
  IBrazilianRealEstateFundIssuer,
  ITrailingReturns,
} from "types/graphqlTypes";

export type BrazilianTrailingReturns = Pick<IBrazilianCompanyIssuer, "__typename"> & {
  securities: Pick<IBrazilianCompanyIssuer["securities"]["0"], "trailingReturns">[];
};

export type AmericanTrailingReturns = Pick<IAmericanCompanyIssuer, "__typename"> & {
  securities: Pick<IAmericanCompanyIssuer["securities"]["0"], "trailingReturns">[];
};

interface TrailingReturnsProps {
  issuer:
    | BrazilianTrailingReturns
    | AmericanTrailingReturns
    | Pick<IBrazilianRealEstateFundIssuer, "__typename" | "trailingReturns">;
  trailingReturns: ITrailingReturns[];
}

export default function TrailingReturns({ issuer, trailingReturns }: TrailingReturnsProps) {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  const trailingReturnsForIssuer = getTrailingReturns(issuer);

  if (!trailingReturnsForIssuer) {
    return null;
  }

  const { oneWeek, oneMonth, oneYear, allTime } = trailingReturnsForIssuer;
  const isNegativeWeek = oneWeek && oneWeek < 0;

  const allOneWeekReturns = trailingReturns.map((node) => node.oneWeek || 0);
  const biggestOneWeekReturn = Math.max(...allOneWeekReturns);
  const smallestOneWeekReturn = Math.min(...allOneWeekReturns);

  const positiveColorScale =
    allOneWeekReturns.length === 0 || biggestOneWeekReturn <= 0
      ? null
      : new ColorScale(0, biggestOneWeekReturn, [tailwindColors.green[100], tailwindColors.green[500]], 0.2);
  const negativeColorScale =
    allOneWeekReturns.length === 0 || Math.abs(smallestOneWeekReturn) === 0
      ? null
      : new ColorScale(0, Math.abs(smallestOneWeekReturn), [tailwindColors.red[100], tailwindColors.red[500]], 0.2);

  const percentageFormatter = new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  const smallPercentageFormatter = new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        asChild={true}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();

          setIsOpen((previous) => !previous);
        }}
      >
        <button
          type="button"
          className={classNames(
            "flex items-center justify-center space-x-1.5 rounded-md px-2 py-1",
            "w-20 font-aeonik text-sm font-bold",
            "text-green-700 dark:text-green-400",
            { "text-red-600 dark:text-red-400": isNegativeWeek },
          )}
          style={{
            backgroundColor:
              (oneWeek || 0) >= 0 && positiveColorScale
                ? positiveColorScale.getColor(oneWeek || 0).toRGBAString()
                : negativeColorScale
                  ? negativeColorScale.getColor(Math.abs(oneWeek || 0)).toRGBAString()
                  : undefined,
          }}
        >
          <span>
            {Math.abs(oneWeek || 0) >= 0.01
              ? percentageFormatter.format(oneWeek || 0)
              : smallPercentageFormatter.format(oneWeek || 0)}
          </span>
          <span
            className={classNames(
              "rounded-full bg-green-600/20 px-1.5 py-0.5 text-xs text-green-700 dark:bg-green-600/30 dark:text-green-400",
              {
                "bg-red-600/20 text-red-700 dark:bg-red-600/30 dark:text-red-400": isNegativeWeek,
              },
            )}
          >
            7D
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        avoidCollisions={true}
        hideWhenDetached={true}
        align="start"
        className="rounded-2xl border border-slate-300 bg-white p-5 shadow-md dark:border-slate-700 dark:bg-slate-800"
      >
        <div className="flex flex-col space-y-2.5">
          <p className="flex items-center space-x-2">
            <CircleIcon className="stroke-red-500 dark:stroke-red-400" size={16} strokeWidth={4} />

            <span className="text-sm font-bold text-gray-900 dark:text-slate-200 md:text-base">
              {intl.formatMessage({ defaultMessage: "Retorno Histórico", id: "tHOpsU" })}
            </span>
          </p>

          <div className="flex space-x-8">
            <TrailingReturn
              value={oneWeek || 0}
              name={intl.formatMessage({ defaultMessage: "1 semana", id: "Z7mLAK" })}
            />
            <TrailingReturn
              value={oneMonth || 0}
              name={intl.formatMessage({ defaultMessage: "1 mês", id: "T0sgAr" })}
            />
            <TrailingReturn
              value={oneYear || 0}
              name={intl.formatMessage({
                defaultMessage: "1 ano",
                id: "Lc4l9T",
              })}
            />
            <TrailingReturn
              value={allTime || 0}
              name={intl.formatMessage({ defaultMessage: "Histórico", id: "ZyJcEI" })}
              mustBePremium={true}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface TrailingReturnProps {
  value: number;
  name: string;
  mustBePremium?: boolean;
}

function TrailingReturn({ value, name, mustBePremium = false }: TrailingReturnProps) {
  const viewer = useSelfie();
  const intl = useIntl();

  const percentageFormatter = new Intl.NumberFormat("en-US", {
    style: "percent",
    maximumFractionDigits: 0,
  });

  const valueFormatter = new Intl.NumberFormat(intl.locale, {
    notation: "compact",
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const isNegativeValue = value < 0;
  const isBigValue = value >= 10000;
  const isLocked = mustBePremium && (!viewer || !viewer.isPremium);

  return (
    <p className="grid grid-cols-1 items-center justify-center">
      {isLocked ? (
        <span className="flex h-6 items-center justify-center md:h-7">
          <LockIcon className="h-4 w-4 stroke-slate-600 dark:stroke-slate-400 md:h-5 md:w-5 " />
        </span>
      ) : (
        <strong
          className={classNames("text-center text-base font-bold text-green-600 dark:text-green-400 md:text-xl", {
            "text-red-600 dark:text-red-400": isNegativeValue,
          })}
        >
          {isBigValue ? `${valueFormatter.format(value)} %` : percentageFormatter.format(value)}
        </strong>
      )}

      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">{name}</span>
    </p>
  );
}
